import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useClub } from "@/contexts/ClubContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, Newspaper, Image, Users, FileText, Info, Clock, Download, ChevronDown, X } from "lucide-react";
import { LinkifyText } from "@/components/LinkifyText";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLUB_DESCRIPTIONS = {
  "bsv-niners": {
    subtitle: "Kugelkunst mit Charakter!",
    intro: "Wir spielen nicht einfach nur Billard – wir zelebrieren es mit Stil, Präzision und einem Augenzwinkern.",
    text: `Der BSV Niners Wels ist der Billardsportverein der NINERS-Familie – ein Treffpunkt für alle, die beim Spiel mit Kö und Kugel Ruhe, Taktik und Geselligkeit schätzen.

Gegründet von echten Billardliebhabern, verbindet unser Verein sportlichen Ehrgeiz mit echtem Teamspirit.
Egal ob Trickshot-Talent, Serien-Locher oder totaler Anfänger – bei uns ist jeder willkommen, der Lust auf Kugeln, Konzentration und gute Gespräche hat.`,
    vision: [
      "Den Billardsport in Wels und Umgebung fördern",
      "Ein Ort zum Wachsen, Lernen & Miteinander-Sein",
      "Turniere, Trainings und gesellige Vereinsabende",
      "Stilvolles Spiel & entspannte Atmosphäre in der NINERS Sportsbar",
    ],
    quote: "Denn bei uns hei\u00DFt\u2019s nicht nur: \u201EBall in die Tasche!\u201C \u2013 sondern auch:\n\u201EBleib locker, bleib fair \u2013 und hab Spa\u00DF beim Spiel!\u201C",
    closing: "Ob 8-Ball, 9-Ball, 14/1 endlos oder einfach ein gemütlicher Abend am Tisch:\nDer BSV Niners Wels freut sich über neue Gesichter mit ruhiger Hand und Freude am Spiel.",
    email: "bsv@niners.at",
    instagram: "@bsv.niners.wels",
  },
  "dsv-niners": {
    subtitle: "Präzision. Freu(n)de. Teamgeist.",
    intro: "Wir werfen nicht nur Pfeile – Der DSV Niners Wels ist ein aktiver Softdart-Verein, gegründet im Jahr 2018, mit einer klaren Vision: Spaß haben, Volltreffer landen und die Welser Dart Welt bereichern!",
    text: `Egal ob blutiger Anfänger, ehemaliger Dart-König oder einfach nur Dart-begeistert – bei uns ist jeder willkommen.
Was bei uns zählt? Fairness, Teamgeist und eine gesunde Portion Schmäh.`,
    vision: [
      "Offene Trainings & interne Turniere",
      "Ligabetrieb & Cup-Teilnahmen",
      "Gemütliche Vereinsabende mit viel Gelächter",
      "Gemeinschaft, in der man sich sofort wie zuhause fühlt",
      "Unsere Homebase – die legendäre NINERS Sportsbar in Wels",
    ],
    visionTitle: "Was dich erwartet:",
    quote: "Seit unserer Gründung bringen wir frischen Wind in die Dartszene von Wels – mit Leidenschaft, Humor und einer guten Dosis Ehrgeiz.",
    closing: "Denn bei uns geht's nicht nur ums Treffen – sondern ums Dabeisein, Mitreden und Mitlachen.\nDu willst Teil davon sein? Dann komm vorbei und erlebe, wie viel Spaß Dart machen kann!",
    email: "dsv@niners.at",
    instagram: "@dsv.niners.wels",
  },
  "sdv-niners": {
    subtitle: "Leidenschaft. Präzision. Gemeinschaft. Mit Steel.",
    intro: "Du liebst Spannung, Wettkampf und gute Gesellschaft? Dann bist du bei uns genau richtig!",
    text: `Der SDV Niners Wels ist ein neuer, engagierter Steeldart-Verein im Herzen von Wels – gegründet aus dem Wunsch, Steeldart den Menschen in Wels und Umgebung näherzubringen.
Ob Anfänger, Hobbyspieler oder ambitionierter Turnierdarter – bei uns findest du ein zweites Zuhause!`,
    vision: [
      "Regelmäßige Trainingsabende in entspannter Atmosphäre",
      "Turniere & Ligaspiele für alle Spielstärken",
      "Ein starkes Teamgefühl und echte Freundschaften",
      "Spaß, Fairness & sportlicher Ehrgeiz",
      "Top-Ausrüstung & professionelle Boards in unserer Homebase, der NINERS Sportsbar",
    ],
    visionTitle: "Wir bieten:",
    quote: "Unsere Mitglieder sind zwischen 18 und 60+ Jahre jung, kommen aus allen Ecken des Lebens – und teilen eine große Leidenschaft: DART!",
    closing: "Du willst mal reinschnuppern? Komm vorbei, wirf ein paar Pfeile und lern uns kennen!\nDenn bei uns gilt: Jeder Treffer zählt – und jeder Mensch auch.",
    email: "sdv@niners.at",
    instagram: "@sdv.niners.wels",
  },
};

const CLUB_IMAGES = {
  "dsv-niners": "/api/images/dsv-hero",
  "sdv-niners": "/api/images/sdv-hero",
  "bsv-niners": "/assets/4ey7g8jx_billiard-balls-blue-pool-table.jpg",
  "vip": "/api/images/vip-hero",
  "perchten": "https://images.unsplash.com/photo-1764776810948-162695576412?w=1200&q=80",
  "soundtheatre": "https://images.unsplash.com/photo-1618613403887-ed08ea9f8f6e?w=1200&q=80",
  "sportuebertragungen": "/assets/6b9ag9sx_Leinwand_Live_Sport_04.jpg",
  "shop": "https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=1200&q=80",
};

export default function ClubPage() {
  const { clubId } = useParams();
  const { setActiveClub, getClubAccent, clubs } = useClub();
  const clubName = clubs.find(c => c.id === clubId)?.name || clubId;
  usePageTitle(clubName);
  const [clubInfo, setClubInfo] = useState(null);
  const [clubDesc, setClubDesc] = useState(null);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    setActiveClub(clubId);
    axios.get(`${API}/clubs/${clubId}`).then(r => {
      setClubInfo(r.data);
      setClubDesc(r.data.description || CLUB_DESCRIPTIONS[clubId] || null);
    }).catch(() => setClubDesc(CLUB_DESCRIPTIONS[clubId] || null));
    axios.get(`${API}/events?club_id=${clubId}`).then(r => setEvents(r.data)).catch(() => {});
    axios.get(`${API}/news?club_id=${clubId}`).then(r => setNews(r.data)).catch(() => {});
    axios.get(`${API}/gallery?club_id=${clubId}`).then(r => setGallery(r.data)).catch(() => {});
  }, [clubId, setActiveClub]);

  const accent = getClubAccent(clubId);
  const club = clubs.find(c => c.id === clubId);

  if (!club) return <div className="py-24 text-center text-muted-foreground">Verein nicht gefunden.</div>;

  return (
    <div data-testid="club-page">
      {/* Club Hero */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img src={resolveImageUrl(CLUB_IMAGES[clubId]) || CLUB_IMAGES[clubId]} alt={club.name}
          onError={e => { e.target.style.display = 'none'; }}
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-end px-4 md:px-8 pb-8 md:pb-12 max-w-6xl mx-auto">
          <p className="text-xs font-medium uppercase tracking-widest mb-2" style={{ color: accent }}>{club.type}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white animate-fade-in-up"
            style={{ fontFamily: 'Oswald' }}>
            {club.name}
          </h1>
        </div>
      </section>

      {/* Content */}
      <div className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue={clubDesc ? "about" : "events"} className="w-full" data-testid="club-tabs">
            <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-8">
              {[
                ...(clubDesc ? [{ value: "about", label: "Über Uns", icon: Info }] : []),
                { value: "events", label: "Termine", icon: CalendarDays },
                { value: "news", label: "Neuigkeiten", icon: Newspaper },
                { value: "gallery", label: "Galerie", icon: Image },
                ...(club?.isSpecial ? [] : [
                  { value: "membership", label: "Mitgliedschaft", icon: Users },
                  { value: "statutes", label: "Statuten", icon: FileText },
                ]),
              ].map(tab => (
                <TabsTrigger key={tab.value} value={tab.value}
                  className="club-tab flex items-center gap-2 px-4 py-2 border border-white/10 text-xs font-bold uppercase tracking-wider"
                  style={{ '--club-accent': accent }}
                  data-testid={`club-tab-${tab.value}`}>
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {clubDesc && (
              <TabsContent value="about">
                <ClubDescription desc={clubDesc} accent={accent} />
              </TabsContent>
            )}

            <TabsContent value="events">
              {events.length === 0 ? (
                <p className="text-muted-foreground">Keine Termine vorhanden.</p>
              ) : (
                <div className="space-y-4">
                  {events.map(event => {
                    const isOpen = expandedEvent === event.id;
                    const imgSrc = resolveImageUrl(event.image_url);
                    const hasDetails = event.description || imgSrc;
                    return (
                      <div key={event.id} className="bg-card border border-white/5 overflow-hidden" data-testid={`club-event-${event.id}`}>
                        <button onClick={() => hasDetails && setExpandedEvent(isOpen ? null : event.id)}
                          className={`w-full p-5 text-left ${hasDetails ? 'hover:bg-white/[0.02] cursor-pointer' : 'cursor-default'} transition-colors`}>
                          <div className="flex items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>{event.title}</h3>
                                {event.featured && <Badge style={{ backgroundColor: accent, color: '#09090b' }} className="text-xs">Featured</Badge>}
                              </div>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />
                                  {new Date(event.date).toLocaleDateString('de-AT', { day: '2-digit', month: 'long', year: 'numeric' })}
                                </span>
                                {event.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {event.time}{event.end_time ? ` - ${event.end_time}` : ''}</span>}
                                {hasDetails && !isOpen && <span className="text-primary ml-auto">mehr Infos</span>}
                              </div>
                            </div>
                            {hasDetails && <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5 border-t border-white/5 pt-4">
                            {imgSrc && (
                              <img src={imgSrc} alt={event.title}
                                onClick={() => setLightboxImg(imgSrc)}
                                onError={e => { e.target.style.display = 'none'; }}
                                className="w-full max-h-80 object-contain bg-black/30 cursor-pointer hover:opacity-80 transition-opacity border border-white/10 mb-4" />
                            )}
                            <p className="text-sm text-muted-foreground whitespace-pre-line mb-4"><LinkifyText text={event.description} /></p>
                            <a href={`${API}/events/${event.id}/ical`} download
                              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors">
                              <Download className="w-3 h-3" /> Zum Kalender hinzufügen
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="news">
              {news.length === 0 ? (
                <p className="text-muted-foreground">Keine Neuigkeiten vorhanden.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {news.map(item => (
                    <div key={item.id} className="bg-card border border-white/5 p-6" data-testid={`club-news-${item.id}`}>
                      <h3 className="text-lg font-bold uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald' }}>{item.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{item.content}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="gallery">
              {gallery.length === 0 ? (
                <p className="text-muted-foreground">Noch keine Fotos vorhanden.</p>
              ) : (
                <div className="space-y-8">
                  {gallery.map(item => {
                    const imgs = item.image_urls?.length ? item.image_urls : (item.image_url ? [item.image_url] : []);
                    return (
                      <div key={item.id} data-testid={`club-gallery-${item.id}`}>
                        <h3 className="text-lg font-bold uppercase tracking-tight mb-1" style={{ fontFamily: 'Oswald' }}>{item.title}</h3>
                        {item.description && <p className="text-sm text-muted-foreground mb-3">{item.description}</p>}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {imgs.map((url, idx) => {
                            const src = resolveImageUrl(url);
                            return (
                              <div key={`${item.id}-${url}`} className="aspect-square overflow-hidden bg-card border border-white/5 cursor-pointer"
                                onClick={() => setLightboxImg(src)} data-testid={`gallery-img-${item.id}-${idx}`}>
                                <img src={src} alt={`${item.title} ${idx + 1}`} className="w-full h-full object-cover img-zoom" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="membership">
              <div className="bg-card border border-white/5 p-6 md:p-8 max-w-2xl" data-testid="club-membership">
                <h2 className="text-2xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald', color: accent }}>
                  Mitglied werden
                </h2>
                <div className="prose prose-invert prose-sm max-w-none">
                  <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {clubInfo?.membership_text || "Informationen zur Mitgliedschaft werden in Kürze veröffentlicht."}
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="statutes">
              <div className="bg-card border border-white/5 p-6 md:p-8 max-w-2xl" data-testid="club-statutes">
                <h2 className="text-2xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald', color: accent }}>
                  Vereinsstatuten
                </h2>
                <div className="prose prose-invert prose-sm max-w-none">
                  <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {clubInfo?.statutes_text || "Die Vereinsstatuten werden in Kürze hier veröffentlicht."}
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)} data-testid="club-lightbox">
          <button onClick={() => setLightboxImg(null)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white">
            <X className="w-6 h-6" />
          </button>
          <img src={lightboxImg} alt="" className="max-w-full max-h-[90vh] object-contain" />
        </div>
      )}
    </div>
  );
}


function ClubDescription({ desc, accent }) {
  if (typeof desc === 'string') {
    return (
      <div data-testid="club-description">
        <div className="max-w-3xl">
          <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{desc}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="club-description">
      <div className="max-w-3xl">
        {desc.subtitle && <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: accent }}>{desc.subtitle}</p>}
        {desc.intro && <p className="text-base md:text-lg font-medium mb-6">{desc.intro}</p>}
        {desc.text && <p className="text-sm text-muted-foreground whitespace-pre-line mb-6">{desc.text}</p>}

        {Array.isArray(desc.vision) && desc.vision.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-semibold mb-2">{desc.visionTitle || "Unsere Vision:"}</p>
            <ul className="space-y-1.5">
              {desc.vision.map((item, i) => (
                <li key={`vision-${item.slice(0,20)}`} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: accent }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {desc.quote && (
          <blockquote className="border-l-2 pl-4 py-1 mb-6 text-sm italic text-muted-foreground whitespace-pre-line" style={{ borderColor: accent }}>
            {desc.quote}
          </blockquote>
        )}

        {desc.closing && <p className="text-sm text-muted-foreground whitespace-pre-line mb-6">{desc.closing}</p>}

        {(desc.email || desc.instagram) && (
          <div className="flex flex-wrap gap-4 text-sm">
            {desc.email && (
              <a href={`mailto:${desc.email}`} className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 font-medium hover:bg-primary/20 transition-colors" data-testid="club-email">
                {desc.email}
              </a>
            )}
            {desc.instagram && (
              <a href={`https://instagram.com/${desc.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-muted-foreground hover:text-foreground transition-colors" data-testid="club-instagram">
                Instagram: {desc.instagram}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
